import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useForm, Controller } from 'react-hook-form';
import { CustomButton } from '../components/CustomButton';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { AppModal } from '../components/AppModal';
import { theme } from '../constants/theme';
import ApiService from '../services/ApiService';
import { alphanumericWithoutSpace, removeSpaces, usernameAtFilterRRegex } from '../utils/helpers';
import { ScreenHeader } from '../components/ScreenHeader';

type ForgotPasswordStep = 'username' | 'otp' | 'password' | 'success';

interface ForgotPasswordFormData {
    username: string;
    otp: string;
    newPassword: string;
    confirmPassword: string;
}

const ForgotPasswordScreen = ({ navigation }: any) => {
    const [currentStep, setCurrentStep] = useState<ForgotPasswordStep>('username');
    const [loading, setLoading] = useState(false);
    const [maskedPhone, setMaskedPhone] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [modal, setModal] = useState({ visible: false, title: '', message: '' });

    const {
        control,
        handleSubmit,
        watch,
        getValues,
        formState: { errors },
    } = useForm<ForgotPasswordFormData>({
        defaultValues: {
            username: '',
            otp: '',
            newPassword: '',
            confirmPassword: '',
        },
        mode: 'onBlur',
    });

    const newPassword = watch('newPassword');

    const handleSendOTP = useCallback(async (data: ForgotPasswordFormData) => {
        setLoading(true);
        try {
            const result = await ApiService.sendForgotPasswordOTP(data.username);

            if (result.success) {
                setMaskedPhone(result.phoneNumber || '');
                setCurrentStep('otp');
            } else {
                setModal({
                    visible: true,
                    title: 'Error',
                    message: result.message || 'Username not found',
                });
            }
        } catch (error: any) {
            setModal({
                visible: true,
                title: 'Error',
                message: error.message || 'Failed to send OTP',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const handleResendOTP = useCallback(async () => {
        const username = getValues('username');
        setLoading(true);
        try {
            const result = await ApiService.sendForgotPasswordOTP(username);

            if (result.success) {
                setModal({
                    visible: true,
                    title: 'Success',
                    message: 'Verification code resent to your WhatsApp',
                });
            } else {
                setModal({
                    visible: true,
                    title: 'Error',
                    message: result.message || 'Failed to resend OTP',
                });
            }
        } catch (error: any) {
            setModal({
                visible: true,
                title: 'Error',
                message: error.message || 'Failed to resend OTP',
            });
        } finally {
            setLoading(false);
        }
    }, [getValues]);

    const handleVerifyOTP = useCallback(async (data: ForgotPasswordFormData) => {
        setLoading(true);
        try {
            // Verify OTP with backend before proceeding
            const result = await ApiService.verifyForgotPasswordOTP(data.username, data.otp);

            if (result.success) {
                // Move to next step only if OTP is valid
                setCurrentStep('password');
            } else {
                setModal({
                    visible: true,
                    title: 'Invalid Code',
                    message: result.message || 'The verification code provided is incorrect or expired.',
                });
            }
        } catch (error: any) {
            setModal({
                visible: true,
                title: 'Error',
                message: error.message || 'Failed to verify OTP',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const handleResetPassword = useCallback(async (data: ForgotPasswordFormData) => {
        setLoading(true);
        try {
            const result = await ApiService.resetPasswordWithOTP(
                data.username,
                data.otp,
                data.newPassword
            );

            if (result.success) {
                setCurrentStep('success');
            } else {
                setModal({
                    visible: true,
                    title: 'Error',
                    message: result.message || 'Failed to reset password',
                });
            }
        } catch (error: any) {
            setModal({
                visible: true,
                title: 'Error',
                message: error.message || 'An unexpected error occurred',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const renderStepIndicator = () => {
        const steps = ['Username', 'OTP', 'Password'];
        const currentStepIndex = currentStep === 'username' ? 0 : currentStep === 'otp' ? 1 : 2;

        return (
            <View style={styles.stepIndicator}>
                {steps.map((step, index) => (
                    <View key={step} style={styles.stepItem}>
                        <View
                            style={[
                                styles.stepCircle,
                                index <= currentStepIndex && styles.stepCircleActive,
                            ]}
                        >
                            <Text
                                style={[
                                    styles.stepNumber,
                                    index <= currentStepIndex && styles.stepNumberActive,
                                ]}
                            >
                                {index + 1}
                            </Text>
                        </View>
                        <Text style={styles.stepLabel}>{step}</Text>
                        {index < steps.length - 1 && (
                            <View
                                style={[
                                    styles.stepLine,
                                    index < currentStepIndex && styles.stepLineActive,
                                ]}
                            />
                        )}
                    </View>
                ))}
            </View>
        );
    };

    if (currentStep === 'success') {
        return (
            <SafeAreaView style={styles.container}>
                <ScreenHeader title="Forgot Password" showBackButton={true} navigation={navigation} />
                <KeyboardAwareScrollView
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.successContainer}>
                        <Icon name="check-circle" size={80} color={theme.colors.success} />
                        <Text style={styles.successTitle}>Password Reset Successfully!</Text>
                        <Text style={styles.successMessage}>
                            Your password has been reset. You can now login with your new password.
                        </Text>
                        <CustomButton
                            title="Back to Login"
                            onPress={() => navigation.goBack()}
                            style={styles.backButton}
                        />
                    </View>
                </KeyboardAwareScrollView>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader title="Forgot Password" showBackButton={true} navigation={navigation} />
            <KeyboardAwareScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.headerContainer}>
                    <View style={styles.iconCircle}>
                        <Icon name="lock-reset" size={50} color={theme.colors.primary} />
                    </View>
                    <Text style={styles.title}>Forgot Password?</Text>
                    <Text style={styles.subtitle}>
                        {currentStep === 'username' && "Enter your username to receive a verification code via WhatsApp"}
                        {currentStep === 'otp' && "Enter the verification code sent to your WhatsApp"}
                        {currentStep === 'password' && "Enter your new password"}
                    </Text>
                </View>

                {currentStep !== 'success' && renderStepIndicator()}

                <View style={styles.formContainer}>
                    {currentStep === 'username' && (
                        <>
                            <Controller
                                control={control}
                                name="username"
                                rules={{
                                    required: 'Username is required',
                                }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <FloatingLabelInput
                                        label="Username*"
                                        value={value}
                                        onChangeText={(text) => onChange(usernameAtFilterRRegex(text))}
                                        onBlur={onBlur}
                                        error={errors.username?.message}
                                        autoCapitalize="none"
                                        autoCorrect={false}
                                    />
                                )}
                            />

                            <CustomButton
                                title="Send Verification Code"
                                onPress={handleSubmit(handleSendOTP)}
                                loading={loading}
                            />
                        </>
                    )}

                    {currentStep === 'otp' && (
                        <>
                            {maskedPhone && (
                                <View style={styles.otpInfo}>
                                    <Icon name="information" size={16} color={theme.colors.info} />
                                    <Text style={styles.otpInfoText}>
                                        Code sent to ****{maskedPhone.slice(-4)}
                                    </Text>
                                </View>
                            )}

                            <Controller
                                control={control}
                                name="otp"
                                rules={{
                                    required: 'Verification code is required',
                                    pattern: {
                                        value: /^\d{6}$/,
                                        message: 'Enter valid 6-digit code',
                                    },
                                }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <FloatingLabelInput
                                        label="Verification Code*"
                                        value={value}
                                        onChangeText={onChange}
                                        onBlur={onBlur}
                                        error={errors.otp?.message}
                                        keyboardType="number-pad"
                                        maxLength={6}
                                        placeholder="Enter 6-digit code"
                                    />
                                )}
                            />

                            <CustomButton
                                title="Verify Code"
                                onPress={handleSubmit(handleVerifyOTP)}
                                loading={loading}
                            />

                            <TouchableOpacity onPress={handleResendOTP} style={styles.resendButton}>
                                <Text style={styles.resendText}>Resend Code</Text>
                            </TouchableOpacity>
                        </>
                    )}

                    {currentStep === 'password' && (
                        <>
                            <Controller
                                control={control}
                                name="newPassword"
                                rules={{
                                    required: 'New password is required',
                                    minLength: {
                                        value: 6,
                                        message: 'Password must be at least 6 characters',
                                    },
                                }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <FloatingLabelInput
                                        label="New Password*"
                                        value={value}
                                        onChangeText={(text) => onChange(removeSpaces(text))}
                                        onBlur={onBlur}
                                        error={errors.newPassword?.message}
                                        secureTextEntry={!showNewPassword}
                                        rightIcon={
                                            <TouchableOpacity onPress={() => setShowNewPassword(!showNewPassword)}>
                                                <Icon
                                                    name={showNewPassword ? 'eye-off' : 'eye'}
                                                    size={20}
                                                    color={theme.colors.textSecondary}
                                                />
                                            </TouchableOpacity>
                                        }
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="confirmPassword"
                                rules={{
                                    required: 'Please confirm your password',
                                    validate: (value) =>
                                        value === newPassword || 'Passwords do not match',
                                }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <FloatingLabelInput
                                        label="Confirm New Password*"
                                        value={value}
                                        onChangeText={(text) => onChange(removeSpaces(text))}
                                        onBlur={onBlur}
                                        error={errors.confirmPassword?.message}
                                        secureTextEntry={!showConfirmPassword}
                                        rightIcon={
                                            <TouchableOpacity onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                <Icon
                                                    name={showConfirmPassword ? 'eye-off' : 'eye'}
                                                    size={20}
                                                    color={theme.colors.textSecondary}
                                                />
                                            </TouchableOpacity>
                                        }
                                    />
                                )}
                            />

                            <CustomButton
                                title="Reset Password"
                                onPress={handleSubmit(handleResetPassword)}
                                loading={loading}
                            />
                        </>
                    )}

                    {/* <CustomButton
                        title="Back to Login"
                        onPress={() => navigation.goBack()}
                        variant="outline"
                        style={styles.cancelButton}
                    /> */}
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type="error"
                title={modal.title}
                message={modal.message}
                onClose={() => setModal({ ...modal, visible: false })}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollContent: {
        flexGrow: 1,
        paddingHorizontal: theme.spacing.lg,
        paddingVertical: theme.spacing.xl,
    },
    headerContainer: {
        alignItems: 'center',
        marginBottom: theme.spacing.lg,
    },
    iconCircle: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: theme.colors.primaryLight + '20',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    title: {
        ...theme.typography.h3,
        color: theme.colors.text,
        marginBottom: theme.spacing.sm,
    },
    subtitle: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        paddingHorizontal: theme.spacing.md,
    },
    stepIndicator: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.xl,
        paddingHorizontal: theme.spacing.md,
    },
    stepItem: {
        flex: 1,
        alignItems: 'center',
        position: 'relative',
    },
    stepCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.border,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.xs,
    },
    stepCircleActive: {
        backgroundColor: theme.colors.primary,
    },
    stepNumber: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
    },
    stepNumberActive: {
        color: theme.colors.surface,
    },
    stepLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    stepLine: {
        position: 'absolute',
        top: 18,
        left: '68%',
        right: '-50%',
        height: 2,
        backgroundColor: theme.colors.border,
    },
    stepLineActive: {
        backgroundColor: theme.colors.primary,
    },
    formContainer: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.md,
    },
    otpInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.info + '10',
        padding: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        marginBottom: theme.spacing.md,
    },
    otpInfoText: {
        ...theme.typography.caption,
        color: theme.colors.info,
        marginLeft: theme.spacing.xs,
    },
    resendButton: {
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
        marginTop: theme.spacing.sm,
    },
    resendText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    cancelButton: {
        marginTop: theme.spacing.sm,
    },
    successContainer: {
        alignItems: 'center',
        paddingTop: theme.spacing.xxl,
    },
    successTitle: {
        ...theme.typography.h4,
        color: theme.colors.success,
        marginTop: theme.spacing.lg,
        marginBottom: theme.spacing.sm,
    },
    successMessage: {
        ...theme.typography.body1,
        color: theme.colors.text,
        textAlign: 'center',
        marginBottom: theme.spacing.xl,
        paddingHorizontal: theme.spacing.md,
    },
    // backButton: {
    //     minWidth: 200,
    // },
});

export default ForgotPasswordScreen;
